import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { CATEGORIES, ROLES, STAGES, toggle } from '../lib/taxonomy';
import Loader from '../components/Loader';

/* Promote your product to members — free during the pilot. Every ad (and every
   edit) is checked by an admin before it shows. Measured by what it leads to:
   connections, signups, customers, funding — not only clicks. */

const STATUS = {
  pending: ['Waiting for review', 'An admin checks every ad before it shows — usually within a day.'],
  active: ['Live', ''],
  paused: ['Paused', ''],
  rejected: ['Needs changes', ''],
  ended: ['Ended', ''],
  removed: ['Taken down', ''],
};

const blank = { name: '', pitch: '', url: 'https://', perk_text: '', code_mode: 'generated',
  audience: { sectors: [], roles: [], stages: [], needs: [] }, team_handles: [] };

function Chips({ options, value, onChange }) {
  return (
    <div className="mp-chips">
      {options.map(([k, t]) => (
        <button key={k} type="button" className={`mp-chip${value.includes(k) ? ' on' : ''}`}
          onClick={() => onChange(toggle(value, k))}>{t}</button>
      ))}
    </div>
  );
}

function AdForm({ initial, needs, onSaved, onCancel, notify }) {
  const [f, setF] = useState(initial);
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Once created, saving again edits it — a failed logo upload must not make a second ad.
  const [adId, setAdId] = useState(initial.id || '');
  const set = (patch) => setF({ ...f, ...patch });
  const aud = (patch) => setF({ ...f, audience: { ...f.audience, ...patch } });

  async function save() {
    setBusy(true);
    try {
      const payload = { name: f.name, pitch: f.pitch, url: f.url, perk_text: f.perk_text, code_mode: f.code_mode,
        audience: f.audience, team_handles: (f.team_text || '').split(',').map((s) => s.trim()).filter(Boolean) };
      const ad = adId ? await api.editAd(adId, payload) : await api.createAd(payload);
      setAdId(ad.id);
      if (logo) {
        try { await api.adLogo(ad.id, logo); } catch (e) {
          notify?.(`Your ad is saved, but the logo didn't upload: ${e.message}`);
          return;
        }
      }
      notify?.('Sent for review — you\'ll get an email when it\'s live.');
      onSaved(ad);
    } catch (e) {
      notify?.(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card pad promo-form">
      <h2 className="sec-title">{initial.id ? 'Edit your ad' : 'Promote your product'}</h2>
      <p className="muted-sm">Free during the pilot. Shown as a small "Sponsored" card to members it fits. An admin
        reviews it first, and again after any edit.</p>

      <h3 className="pf-step">1 · Your product</h3>
      <label className="flabel">Name</label>
      <input className="in" maxLength={80} value={f.name} onChange={(e) => set({ name: e.target.value })} />
      <label className="flabel">One-line pitch <span className="muted-sm">{f.pitch.length}/120</span></label>
      <input className="in" maxLength={120} value={f.pitch} placeholder="e.g. Payment rails for startups in Southeast Asia"
        onChange={(e) => set({ pitch: e.target.value })} />
      <label className="flabel">Website (https)</label>
      <input className="in" maxLength={500} value={f.url} onChange={(e) => set({ url: e.target.value })} />
      <p className="muted-sm">Checked against Google's list of unsafe sites. No shortened links.</p>
      <label className="flabel">Logo (optional, PNG/JPEG/WebP, under 2 MB)</label>
      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogo(e.target.files?.[0] || null)} />

      <h3 className="pf-step">2 · A perk for B/N members</h3>
      <input className="in" maxLength={160} value={f.perk_text} placeholder="e.g. 20% off your first year"
        onChange={(e) => set({ perk_text: e.target.value })} />
      <label className="flabel">Codes</label>
      <select className="in" value={f.code_mode} onChange={(e) => set({ code_mode: e.target.value })}>
        <option value="generated">B/N creates a unique code for each member who claims it</option>
        <option value="uploaded">I'll upload my own codes (e.g. from Stripe)</option>
        <option value="none">No code — the link is the perk</option>
      </select>
      <p className="muted-sm">Unique codes let you confirm real signups: mark a code redeemed when it's used.</p>

      <h3 className="pf-step">3 · Who should see it</h3>
      <p className="muted-sm">Leave a group empty to include everyone. Matching happens inside B/N — you never see
        who was shown your ad unless they ask to talk or use their code.</p>
      <label className="flabel">Sectors</label>
      <Chips options={CATEGORIES.map((c) => [c, c])} value={f.audience.sectors} onChange={(v) => aud({ sectors: v })} />
      <label className="flabel">Roles</label>
      <Chips options={ROLES.map((r) => [r, r])} value={f.audience.roles} onChange={(v) => aud({ roles: v })} />
      <label className="flabel">Stages</label>
      <Chips options={STAGES.map((s) => [s, s])} value={f.audience.stages} onChange={(v) => aud({ stages: v })} />
      <label className="flabel">People looking for</label>
      <Chips options={needs} value={f.audience.needs} onChange={(v) => aud({ needs: v })} />
      <label className="flabel">Teammates for "Talk to the team" (B/N handles, optional)</label>
      <input className="in" value={f.team_text || ''} placeholder="e.g. maria, paolo" onChange={(e) => set({ team_text: e.target.value })} />

      <div className="sp-preview">
        <span className="muted-sm">Preview</span>
        <div className="card pad sponsored">
          <div className="sp-head"><span className="sp-label">Sponsored · member perk</span></div>
          <div className="sp-body"><span className="sp-logo sp-initial">{(f.name || '?')[0]}</span>
            <div><b>{f.name || 'Your product'}</b><p className="sp-pitch">{f.pitch || 'Your one-line pitch'}</p>
              {f.perk_text && <p className="sp-perk">🎁 {f.perk_text}</p>}</div></div>
        </div>
      </div>

      <div className="mp-actions">
        <button className="btn primary" disabled={busy || f.name.trim().length < 2 || f.pitch.length < 5} onClick={save}>
          {busy ? 'Sending…' : 'Submit for review'}
        </button>
        <button className="btn quiet" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function Bars({ daily }) {
  if (!daily.length) return <p className="muted-sm">No views yet.</p>;
  const max = Math.max(1, ...daily.map((d) => d.views));
  return (
    <div className="ad-bars" role="img" aria-label="Views and clicks per day">
      {daily.map((d) => (
        <div key={d.day} className="ad-bar" title={`${d.day}: ${d.views} views, ${d.clicks} clicks`}>
          <span style={{ height: `${(d.views / max) * 100}%` }} />
          <em style={{ height: `${(d.clicks / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
}

function AdDetail({ id, notify, onBack, onEdit }) {
  const [s, setS] = useState(null);
  const [code, setCode] = useState('');
  const [upload, setUpload] = useState('');
  const load = useCallback(() => api.adStats(id).then(setS).catch((e) => notify?.(e.message)), [id, notify]);
  useEffect(() => { load(); }, [load]);
  if (!s) return <Loader />;
  const { ad, funnel: f } = s;
  const [label, hint] = STATUS[ad.status] || [ad.status, ''];
  const act = async (fn, ok) => { try { await fn(); notify?.(ok); load(); } catch (e) { notify?.(e.message); } };

  return (
    <div className="promo-detail">
      <button className="linklike back" onClick={onBack}>← My ads</button>
      <h2 className="sec-title">{ad.name} <span className={`ad-status ${ad.status}`}>{label}</span></h2>
      {(hint || ad.review_note || ad.paused_reason) && <p className="muted-sm">{ad.paused_reason || ad.review_note || hint}</p>}
      <div className="mr-actions">
        <button className="btn ghost sm" onClick={() => onEdit(ad)}>Edit</button>
        {ad.status === 'active' && <button className="btn quiet sm" onClick={() => act(() => api.adManage(ad.id, 'pause'), 'Paused')}>Pause</button>}
        {ad.status === 'paused' && <button className="btn quiet sm" onClick={() => act(() => api.adManage(ad.id, 'resume'), 'Running again')}>Resume</button>}
        {!['ended', 'removed'].includes(ad.status) && <button className="btn quiet sm"
          onClick={() => window.confirm('End this ad? You can create a new one later.') && act(() => api.adManage(ad.id, 'end'), 'Ended')}>End</button>}
      </div>
      <p className="muted-sm">Perk page to share with members: <a href={ad.perk_page}>{ad.perk_page}</a></p>

      <section className="card pad">
        <h3 className="ad-h">What it's leading to</h3>
        <div className="ad-funnel">
          {[['Seen', f.views], ['Visits', f.clicks], ['Perks claimed', f.perk_claims], ['Asked to talk', f.talk_requests],
            ['Connected', f.connected], ['Replied', f.replied]].map(([t, n]) => (
              <div key={t} className="ad-step"><b>{n}</b><span>{t}</span></div>
          ))}
        </div>
        <p className="muted-sm">{f.viewers} members saw it · click rate {f.click_rate == null ? '—' : `${(f.click_rate * 100).toFixed(1)}%`} · hidden by {f.hides}</p>
        <Bars daily={s.daily} />
      </section>

      <section className="card pad">
        <h3 className="ad-h">Outcomes</h3>
        {Object.keys(s.outcomes).length === 0 && <p className="muted-sm">None reported yet. Members are asked 14 days after they claim your perk or talk to you.</p>}
        <div className="ad-grid">
          {Object.entries(s.outcomes).map(([k, o]) => (
            <div key={k} className="ad-stat"><b>{o.count}</b><span>{o.label}{o.confirmed ? ` · ${o.confirmed} confirmed` : ''}</span></div>
          ))}
        </div>
        {s.outcome_list.map((o) => (
          <div key={o.id} className="invite-item">
            <div className="invite-main"><b>{o.member}: {o.label}</b>
              <span className="muted-sm">{o.value}{o.note ? ` · “${o.note}”` : ''} · {o.confirmed ? 'confirmed' : 'not confirmed yet'}</span></div>
            {o.can_confirm && !o.confirmed && (
              <button className="btn quiet sm" onClick={() => act(() => api.adConfirmOutcome(ad.id, o.id, true), 'Confirmed')}>Confirm</button>
            )}
          </div>
        ))}
      </section>

      {ad.code_mode !== 'none' && (
        <section className="card pad">
          <h3 className="ad-h">Codes</h3>
          <p className="muted-sm">{s.codes.claimed} claimed · {s.codes.redeemed} redeemed
            {ad.code_mode === 'uploaded' ? ` · ${s.codes.unclaimed_uploaded} still available` : ''}</p>
          <div className="invite-row">
            <input className="in" value={code} placeholder="A code a customer used" onChange={(e) => setCode(e.target.value)} />
            <button className="btn ghost" disabled={!code.trim()}
              onClick={() => act(async () => { await api.adRedeem(ad.id, code); setCode(''); }, 'Marked redeemed — counted as a signup')}>Mark redeemed</button>
          </div>
          {ad.code_mode === 'uploaded' && (
            <>
              <textarea className="ta" rows={3} value={upload} placeholder="Paste codes, one per line"
                onChange={(e) => setUpload(e.target.value)} />
              <button className="btn quiet sm" disabled={!upload.trim()}
                onClick={() => act(async () => { const r = await api.adUploadCodes(ad.id, upload); setUpload(''); notify?.(`${r.added} codes added`); }, 'Codes added')}>Add codes</button>
            </>
          )}
        </section>
      )}

      <section className="card pad">
        <h3 className="ad-h">Who's interested</h3>
        {Object.keys(s.audience.sectors).length + Object.keys(s.audience.roles).length === 0
          ? <p className="muted-sm">{s.audience.note}</p>
          : <p className="muted-sm">Visits by sector: {Object.entries(s.audience.sectors).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}<br />
              By role: {Object.entries(s.audience.roles).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}</p>}
        {s.feedback.length > 0 && <p className="muted-sm">Feedback from members who hid it: {s.feedback.map((t) => `“${t}”`).join(' · ')}</p>}
      </section>
    </div>
  );
}

export default function Promote({ notify }) {
  const [ads, setAds] = useState(null);
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(null);
  const [needs, setNeeds] = useState([]);
  const load = useCallback(() => api.myAds().then(setAds).catch((e) => notify?.(e.message)), [notify]);
  useEffect(() => {
    load();
    api.matchProfile().then((m) => setNeeds(Object.entries(m.vocabulary.exchange).map(([k, v]) => [k, v.need]))).catch(() => {});
  }, [load]);

  if (form) {
    return <div className="page promote"><AdForm initial={form} needs={needs} notify={notify}
      onCancel={() => setForm(null)} onSaved={() => { setForm(null); load(); }} /></div>;
  }
  if (open) {
    return <div className="page promote"><AdDetail id={open} notify={notify} onBack={() => { setOpen(null); load(); }}
      onEdit={(ad) => setForm({ ...blank, ...ad, audience: { ...blank.audience, ...(ad.audience || {}) }, perk_text: ad.perk, team_text: ad.team.map((t) => t.handle).join(', ') })} /></div>;
  }
  return (
    <div className="page promote">
      <div className="invites-head">
        <h1 className="sec-title">My ads</h1>
        <button className="btn primary sm" onClick={() => setForm(blank)}>Promote your product</button>
      </div>
      <p className="muted-sm">Free during the pilot. Up to two ads at a time. Measured by what they lead to — visits,
        perks claimed, conversations, signups, customers, funding.</p>
      {ads && ads.length === 0 && <div className="card pad"><p>You're not promoting anything yet.</p>
        <p className="muted-sm">Got a tool other founders would use? Offer B/N members a perk and see who bites.</p></div>}
      {(ads || []).map((a) => {
        const [label] = STATUS[a.status] || [a.status];
        return (
          <button key={a.id} className="card pad promo-row" onClick={() => setOpen(a.id)}>
            <b>{a.name}</b> <span className={`ad-status ${a.status}`}>{label}</span>
            <span className="muted-sm">{a.funnel.views} seen · {a.funnel.clicks} visits · {a.funnel.perk_claims} perks · {a.funnel.connected} connected</span>
          </button>
        );
      })}
    </div>
  );
}
